import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Plus, Trash2, Check, ShieldCheck, Loader2, Sparkles,
  AlertCircle, Wand2, Undo2, Save, FileText,
} from "lucide-react";
import { cvApi } from "@/lib/api";
import { CvWriteAssist } from "@/components/cv/CvWriteAssist";
import {
  STEPS, SITUATIONS, WORK_MODES, EDU_STATUS, LANG_SUGGEST, LANG_LEVELS,
  TOOL_LEVELS, DRIVING_CATEGORIES, DOC_LANGS, suggestSkills,
} from "@/components/cv/cvInterviewContent";

const field = "w-full rounded-field border border-brand-line bg-brand-bg px-4 py-2.5 text-[15px] text-brand-text outline-none transition-colors focus:border-brand-accent";
const label = "mb-1.5 block text-sm font-medium text-brand-text";
const optional = <span className="ml-1 text-[12px] font-normal text-brand-muted">(opcjonalne)</span>;

// Duży, czytelny kafelek wyboru (pojedynczy lub wielokrotny) + opcja „Inne".
function TileGroup({ options, value, multi = false, onChange, columns = "sm:grid-cols-2", allowOther = false, otherValue = "", onOther, testId }) {
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const selected = multi ? (Array.isArray(value) ? value : []) : value;
  const isOn = (v) => (multi ? selected.includes(v) : selected === v);
  const pick = (v) => {
    if (multi) onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    else onChange(selected === v ? "" : v);
  };
  const otherOn = allowOther && (multi ? selected.includes("Inne") : selected === "Inne");
  return (
    <div>
      <div className={`grid grid-cols-1 gap-2.5 ${columns}`} data-testid={testId}>
        {norm.map((o, i) => {
          const on = isOn(o.value);
          return (
            <button
              key={o.value}
              type="button"
              data-testid={`${testId}-${i}`}
              aria-pressed={on}
              onClick={() => pick(o.value)}
              className={`relative flex flex-col rounded-panel border p-4 text-left transition-all ${
                on ? "border-brand-accent bg-brand-accent/10 ring-1 ring-brand-accent" : "border-brand-line bg-brand-surface hover:border-brand-accent/50"
              }`}
            >
              <span className="flex items-center justify-between">
                <span className="text-[15px] font-semibold text-brand-text">{o.label}</span>
                <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${on ? "border-brand-accent bg-brand-accent text-brand-on" : "border-brand-line"}`}>
                  {on && <Check size={13} strokeWidth={3} />}
                </span>
              </span>
              {o.desc && <span className="mt-1 text-[13px] text-brand-sec">{o.desc}</span>}
            </button>
          );
        })}
        {allowOther && (
          <button
            type="button"
            data-testid={`${testId}-other`}
            aria-pressed={otherOn}
            onClick={() => pick("Inne")}
            className={`relative flex items-center justify-between rounded-panel border p-4 text-left transition-all ${
              otherOn ? "border-brand-accent bg-brand-accent/10 ring-1 ring-brand-accent" : "border-brand-line bg-brand-surface hover:border-brand-accent/50"
            }`}
          >
            <span className="text-[15px] font-semibold text-brand-text">Inne</span>
            <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${otherOn ? "border-brand-accent bg-brand-accent text-brand-on" : "border-brand-line"}`}>
              {otherOn && <Check size={13} strokeWidth={3} />}
            </span>
          </button>
        )}
      </div>
      {otherOn && (
        <input
          data-testid={`${testId}-other-input`}
          className={`${field} mt-2.5`}
          value={otherValue}
          onChange={(e) => onOther(e.target.value)}
          placeholder="Wpisz własną odpowiedź"
        />
      )}
    </div>
  );
}

export default function CvInterview() {
  const { cvId } = useParams();
  const navigate = useNavigate();
  const [cv, setCv] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [assist, setAssist] = useState(null);
  const [summaryUndo, setSummaryUndo] = useState(null);
  const [expUndo, setExpUndo] = useState(null);
  const initRef = useRef(false);
  const summaryEditedRef = useRef(false);
  const expEditedRef = useRef(new Set());
  const logAssist = (event, fld) => cvApi.assistEvent({ event, field: fld });

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      try {
        if (cvId) {
          const d = await cvApi.get(cvId);
          setCv(d); setStepIndex(d.step_index || 0);
        } else {
          const { draft } = await cvApi.latestDraft("nowe");
          if (draft) { setCv(draft); setStepIndex(draft.step_index || 0); }
          else {
            const d = await cvApi.create({ tryb: "nowe" });
            setCv(d);
            navigate(`/cv/kreator/${d.id}`, { replace: true });
          }
        }
      } catch {
        setError("Nie udało się wczytać wywiadu. Odśwież stronę lub spróbuj ponownie.");
      }
    })();
  }, [cvId, navigate]);

  const situationKey = cv?.situation || "";
  const isFirstJob = situationKey === "pierwsza";

  const steps = useMemo(() => {
    return STEPS.map((s) => {
      if (s.key === "experience" && isFirstJob) return { ...s, title: "Doświadczenie, praktyki i projekty" };
      return s;
    });
  }, [isFirstJob]);

  const patchLocal = (partial) => setCv((c) => ({ ...c, ...partial }));
  const patchBasics = (partial) => setCv((c) => ({ ...c, basics: { ...(c.basics || {}), ...partial } }));

  const snapshot = () => ({
    situation: cv.situation, situation_other: cv.situation_other,
    target_role: cv.target_role, target_industry: cv.target_industry,
    target_location: cv.target_location, target_country: cv.target_country,
    work_mode: cv.work_mode, job_offer: cv.job_offer,
    basics: cv.basics, summary: cv.summary,
    experience: cv.experience, education: cv.education,
    skills: cv.skills, tools: cv.tools, languages: cv.languages,
    courses: cv.courses, certificates: cv.certificates, licenses: cv.licenses,
    driving: cv.driving, projects: cv.projects, interests: cv.interests,
    doc_lang: cv.doc_lang,
  });

  const persist = async (extra = {}, attempt = 0) => {
    if (!cv?.id) return;
    setSaving(true); setSaveError(false);
    try {
      await cvApi.patch(cv.id, { ...snapshot(), ...extra });
      setSavedAt(Date.now()); setSaveError(false);
    } catch {
      setSaveError(true);
      if (attempt < 1) setTimeout(() => persist(extra, attempt + 1), 3000);
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    const ni = Math.min(stepIndex + 1, steps.length - 1);
    const completed = Array.from(new Set([...(cv.completed_steps || []), steps[stepIndex].key]));
    patchLocal({ completed_steps: completed, step_index: ni });
    setStepIndex(ni);
    await persist({ step_index: ni, completed_steps: completed });
  };
  const goBack = async () => {
    const pi = Math.max(stepIndex - 1, 0);
    setStepIndex(pi);
    await persist({ step_index: pi });
  };
  const saveLater = async () => {
    await persist({ step_index: stepIndex });
    navigate("/cv/moje");
  };
  const prepare = async () => { await persist({ status: "ready" }); setDone(true); };

  const addItem = (key, blank) => patchLocal({ [key]: [...(cv[key] || []), blank] });
  const updItem = (key, i, partial) => patchLocal({ [key]: (cv[key] || []).map((x, j) => (j === i ? { ...x, ...partial } : x)) });
  const delItem = (key, i) => patchLocal({ [key]: (cv[key] || []).filter((_, j) => j !== i) });
  const addTag = (key, val) => { const v = (val || "").trim(); if (v && !(cv[key] || []).includes(v)) patchLocal({ [key]: [...(cv[key] || []), v] }); };
  const delTag = (key, val) => patchLocal({ [key]: (cv[key] || []).filter((x) => x !== val) });

  const insertSummary = (text) => { setSummaryUndo(cv.summary || ""); summaryEditedRef.current = false; patchLocal({ summary: text }); persist({ summary: text }); };
  const undoSummary = () => { patchLocal({ summary: summaryUndo || "" }); persist({ summary: summaryUndo || "" }); setSummaryUndo(null); };
  const onSummaryChange = (v) => {
    patchLocal({ summary: v });
    if (summaryUndo !== null && !summaryEditedRef.current) { summaryEditedRef.current = true; logAssist("edited_after_insert", "summary"); }
  };
  const insertExpBullets = (i, text) => {
    const prev = (cv.experience || [])[i]?.bullets || "";
    const next = (cv.experience || []).map((x, j) => (j === i ? { ...x, bullets: text } : x));
    expEditedRef.current.delete(i);
    setExpUndo({ index: i, prev }); patchLocal({ experience: next }); persist({ experience: next });
  };
  const onExpBulletsChange = (i, v) => {
    updItem("experience", i, { bullets: v });
    if (expUndo && expUndo.index === i && !expEditedRef.current.has(i)) { expEditedRef.current.add(i); logAssist("edited_after_insert", "experience"); }
  };
  const undoExpBullets = () => {
    if (!expUndo) return;
    const next = (cv.experience || []).map((x, j) => (j === expUndo.index ? { ...x, bullets: expUndo.prev } : x));
    patchLocal({ experience: next }); persist({ experience: next }); setExpUndo(null);
  };

  // Autozapis z debounce dla WSZYSTKICH pól (także krok 9: podsumowanie i język dokumentu),
  // aby żadna zmiana nie przepadła przy odświeżeniu/zamknięciu karty przed Dalej/Zapisz.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!cv?.id) return;
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    const t = setTimeout(() => { persist(); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cv?.situation, cv?.situation_other, cv?.target_role, cv?.target_industry,
    cv?.target_location, cv?.target_country, cv?.work_mode, cv?.job_offer,
    cv?.basics, cv?.summary, cv?.experience, cv?.education, cv?.skills, cv?.tools,
    cv?.languages, cv?.courses, cv?.certificates, cv?.licenses, cv?.driving,
    cv?.projects, cv?.interests, cv?.doc_lang,
  ]);

  if (error && !cv) return <div data-testid="cv-interview-error" className="rounded-panel border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-300">{error}</div>;
  if (!cv) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-accent" /></div>;

  const step = steps[stepIndex];
  const progress = Math.round((stepIndex / (steps.length - 1)) * 100);
  const canNext = !(step.key === "situation" && !situationKey);

  return (
    <div data-testid="cv-interview">
      {/* Postęp + autozapis */}
      <div className="mb-5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[13px] text-brand-muted">
          <span data-testid="cv-step-counter">Krok {stepIndex + 1} z {steps.length} · {step.title}</span>
          <span data-testid="cv-autosave-indicator" className={`inline-flex items-center gap-1.5 ${saveError && !saving ? "text-red-400" : ""}`}>
            {saving ? (<><Loader2 size={13} className="animate-spin" /> Zapisywanie…</>)
              : saveError ? (<><AlertCircle size={13} /> Nie udało się zapisać — spróbujemy ponownie</>)
              : savedAt ? (<><Check size={13} className="text-brand-accent" /> Zapisano roboczo</>)
              : "Autozapis włączony"}
          </span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-brand-line">
          <div className="h-full rounded-full bg-brand-accent transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <h2 data-testid="cv-step-title" className="font-display text-xl font-semibold text-brand-text sm:text-2xl">{step.title}</h2>
      <p className="mt-1 text-sm text-brand-sec">{step.desc}</p>

      <div className="mt-6 min-h-[220px]">
        {step.key === "situation" && (
          <TileGroup
            testId="cv-situation-option"
            options={SITUATIONS.map((s) => ({ value: s.key, label: s.label, desc: s.desc }))}
            value={situationKey === "Inne" ? "Inne" : situationKey}
            onChange={(v) => patchLocal({ situation: v })}
            allowOther
            otherValue={cv.situation_other || ""}
            onOther={(v) => patchLocal({ situation_other: v })}
          />
        )}

        {step.key === "target" && (
          <div className="grid gap-4">
            <div><label className={label}>Stanowisko lub obszar</label><input data-testid="cv-target-role" className={field} value={cv.target_role || ""} onChange={(e) => patchLocal({ target_role: e.target.value })} placeholder="np. Specjalista ds. marketingu cyfrowego" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={label}>Branża {optional}</label><input data-testid="cv-target-industry" className={field} value={cv.target_industry || ""} onChange={(e) => patchLocal({ target_industry: e.target.value })} placeholder="np. E-commerce" /></div>
              <div><label className={label}>Lokalizacja {optional}</label><input data-testid="cv-target-location" className={field} value={cv.target_location || ""} onChange={(e) => patchLocal({ target_location: e.target.value })} placeholder="np. Warszawa" /></div>
            </div>
            <div>
              <label className={label}>Tryb pracy {optional}</label>
              <TileGroup testId="cv-workmode" columns="sm:grid-cols-3" options={WORK_MODES} value={cv.work_mode || ""} onChange={(v) => patchLocal({ work_mode: v })} />
            </div>
            <div><label className={label}>Konkretna oferta pracy {optional}</label><textarea data-testid="cv-job-offer" className={field} rows={3} value={cv.job_offer || ""} onChange={(e) => patchLocal({ job_offer: e.target.value })} placeholder="Wklej treść ogłoszenia, jeśli chcesz dopasować CV do konkretnej oferty." /></div>
          </div>
        )}

        {step.key === "contact" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className={label}>Imię</label><input data-testid="cv-basics-first-name" className={field} value={cv.basics?.first_name || ""} onChange={(e) => patchBasics({ first_name: e.target.value })} /></div>
            <div><label className={label}>Nazwisko</label><input data-testid="cv-basics-last-name" className={field} value={cv.basics?.last_name || ""} onChange={(e) => patchBasics({ last_name: e.target.value })} /></div>
            <div><label className={label}>Telefon</label><input data-testid="cv-basics-phone" className={field} value={cv.basics?.phone || ""} onChange={(e) => patchBasics({ phone: e.target.value })} /></div>
            <div><label className={label}>E-mail</label><input data-testid="cv-basics-email" className={field} value={cv.basics?.email || ""} onChange={(e) => patchBasics({ email: e.target.value })} /></div>
            <div><label className={label}>Miejscowość</label><input data-testid="cv-basics-city" className={field} value={cv.basics?.city || ""} onChange={(e) => patchBasics({ city: e.target.value })} /></div>
            <div><label className={label}>Link zawodowy {optional}</label><input data-testid="cv-basics-link" className={field} value={(cv.basics?.links || [])[0] || ""} onChange={(e) => patchBasics({ links: e.target.value ? [e.target.value] : [] })} placeholder="np. linkedin.com/in/…" /></div>
          </div>
        )}

        {step.key === "experience" && (
          <div className="space-y-4">
            {isFirstJob && <p className="rounded-field border border-brand-line bg-brand-surface px-4 py-3 text-sm text-brand-sec">Nie masz jeszcze pracy zawodowej? Dodaj staże, praktyki, projekty lub wolontariat. Ten krok możesz też pominąć.</p>}
            {(cv.experience || []).map((x, i) => (
              <div key={i} data-testid="cv-experience-item" className="rounded-card border border-brand-line bg-brand-surface p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <input className={field} placeholder={isFirstJob ? "Rola / projekt / wolontariat" : "Stanowisko / rola"} value={x.role || ""} onChange={(e) => updItem("experience", i, { role: e.target.value })} />
                  <input className={field} placeholder="Firma / organizacja" value={x.company || ""} onChange={(e) => updItem("experience", i, { company: e.target.value })} />
                  <input className={field} placeholder="Od (np. 03.2021)" value={x.start || ""} onChange={(e) => updItem("experience", i, { start: e.target.value })} />
                  <input className={field} placeholder="Do (np. obecnie)" value={x.end || ""} onChange={(e) => updItem("experience", i, { end: e.target.value })} />
                </div>
                <textarea className={`${field} mt-3`} rows={3} placeholder="Obowiązki / osiągnięcia (każdy punkt w nowej linii)" value={x.bullets || ""} onChange={(e) => onExpBulletsChange(i, e.target.value)} />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button type="button" data-testid={`cv-assist-open-experience-${i}`} onClick={() => setAssist({ field: "experience", expIndex: i })} className="inline-flex items-center gap-1.5 rounded-full border border-brand-accent/40 bg-brand-accent/[0.06] px-3 py-1.5 text-[13px] font-medium text-brand-accent transition-colors hover:bg-brand-accent/10"><Wand2 size={13} /> Pomóż mi napisać</button>
                  {expUndo && expUndo.index === i && (
                    <button type="button" data-testid={`cv-assist-undo-experience-${i}`} onClick={undoExpBullets} className="inline-flex items-center gap-1.5 text-[13px] text-brand-muted hover:text-brand-text"><Undo2 size={13} /> Cofnij zmianę</button>
                  )}
                  <button type="button" data-testid="cv-experience-remove" onClick={() => delItem("experience", i)} className="ml-auto inline-flex items-center gap-1 text-[13px] text-brand-muted hover:text-red-300"><Trash2 size={13} /> Usuń</button>
                </div>
              </div>
            ))}
            <button type="button" data-testid="cv-add-experience" onClick={() => addItem("experience", { role: "", company: "", start: "", end: "", bullets: "" })} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60"><Plus size={15} /> {isFirstJob ? "Dodaj praktykę, projekt lub wolontariat" : "Dodaj doświadczenie"}</button>
          </div>
        )}

        {step.key === "education" && (
          <div className="space-y-4">
            {(cv.education || []).map((x, i) => (
              <div key={i} data-testid="cv-education-item" className="rounded-card border border-brand-line bg-brand-surface p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <input className={field} placeholder="Szkoła / uczelnia" value={x.school || ""} onChange={(e) => updItem("education", i, { school: e.target.value })} />
                  <input className={field} placeholder="Kierunek / profil" value={x.field || ""} onChange={(e) => updItem("education", i, { field: e.target.value })} />
                  <input className={field} placeholder="Od" value={x.start || ""} onChange={(e) => updItem("education", i, { start: e.target.value })} />
                  <input className={field} placeholder="Do" value={x.end || ""} onChange={(e) => updItem("education", i, { end: e.target.value })} />
                </div>
                <div className="mt-3">
                  <p className="mb-1.5 text-[13px] text-brand-sec">Status nauki</p>
                  <TileGroup testId={`cv-edu-status-${i}`} columns="sm:grid-cols-3" options={EDU_STATUS} value={x.status || ""} onChange={(v) => updItem("education", i, { status: v })} />
                </div>
                <button type="button" data-testid="cv-education-remove" onClick={() => delItem("education", i)} className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand-muted hover:text-red-300"><Trash2 size={13} /> Usuń</button>
              </div>
            ))}
            <button type="button" data-testid="cv-add-education" onClick={() => addItem("education", { school: "", field: "", start: "", end: "", status: "" })} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60"><Plus size={15} /> Dodaj wykształcenie</button>
          </div>
        )}

        {step.key === "skills" && (
          <div className="space-y-6">
            <div>
              <p className={label}>Propozycje dla „{cv.target_role || "Twojego stanowiska"}" — kliknij, aby dodać</p>
              <div className="flex flex-wrap gap-2" data-testid="cv-skill-suggestions">
                {suggestSkills(cv.target_role).map((s, i) => {
                  const on = (cv.skills || []).includes(s);
                  return (
                    <button key={s} type="button" data-testid={`cv-skill-suggest-${i}`} onClick={() => (on ? delTag("skills", s) : addTag("skills", s))} className={`rounded-full border px-3.5 py-2 text-[13px] font-medium transition-colors ${on ? "border-brand-accent bg-brand-accent/10 text-brand-text" : "border-brand-line text-brand-sec hover:text-brand-text"}`}>
                      {on ? "✓ " : "+ "}{s}
                    </button>
                  );
                })}
              </div>
            </div>
            <TagAdder label="Twoje umiejętności" testId="cv-skill" values={cv.skills || []} onAdd={(v) => addTag("skills", v)} onDel={(v) => delTag("skills", v)} placeholder="Dodaj własną umiejętność — Enter" />
            <div>
              <p className={label}>Narzędzia i programy {optional}</p>
              {(cv.tools || []).map((t, i) => (
                <div key={i} className="mb-2 flex flex-wrap items-center gap-2" data-testid="cv-tool-item">
                  <input className={`${field} max-w-xs`} placeholder="np. Excel, Photoshop, SAP" value={t.name || ""} onChange={(e) => updItem("tools", i, { name: e.target.value })} />
                  <div className="flex gap-1.5">
                    {TOOL_LEVELS.map((lv) => (
                      <button key={lv} type="button" data-testid={`cv-tool-level-${i}-${lv}`} onClick={() => updItem("tools", i, { level: lv })} className={`rounded-full border px-2.5 py-1.5 text-[12px] transition-colors ${t.level === lv ? "border-brand-accent bg-brand-accent/10 text-brand-text" : "border-brand-line text-brand-sec hover:text-brand-text"}`}>{lv}</button>
                    ))}
                  </div>
                  <button type="button" onClick={() => delItem("tools", i)} className="text-brand-muted hover:text-red-300"><Trash2 size={15} /></button>
                </div>
              ))}
              <button type="button" data-testid="cv-add-tool" onClick={() => addItem("tools", { name: "", level: "" })} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60"><Plus size={15} /> Dodaj narzędzie</button>
            </div>
          </div>
        )}

        {step.key === "languages" && (
          <div className="space-y-4">
            <p className="text-sm text-brand-sec">Dodaj język, którym się posługujesz, i wskaż poziom jednym kliknięciem.</p>
            <div className="flex flex-wrap gap-2" data-testid="cv-lang-suggestions">
              {LANG_SUGGEST.map((l) => {
                const exists = (cv.languages || []).some((x) => x.name === l);
                return <button key={l} type="button" data-testid={`cv-lang-suggest-${l}`} disabled={exists} onClick={() => addItem("languages", { name: l, level: "" })} className={`rounded-full border px-3.5 py-2 text-[13px] font-medium transition-colors ${exists ? "cursor-default border-brand-line text-brand-muted opacity-60" : "border-brand-line text-brand-sec hover:text-brand-text"}`}>+ {l}</button>;
              })}
            </div>
            {(cv.languages || []).map((x, i) => (
              <div key={i} data-testid="cv-language-item" className="rounded-card border border-brand-line bg-brand-surface p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <input className={`${field} max-w-xs`} placeholder="Język" value={x.name || ""} onChange={(e) => updItem("languages", i, { name: e.target.value })} />
                  <button type="button" onClick={() => delItem("languages", i)} className="ml-auto text-brand-muted hover:text-red-300"><Trash2 size={15} /></button>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {LANG_LEVELS.map((lv) => (
                    <button key={lv.key} type="button" data-testid={`cv-lang-level-${i}-${lv.key}`} onClick={() => updItem("languages", i, { level: lv.label })} className={`rounded-full border px-3 py-1.5 text-[12.5px] transition-colors ${x.level === lv.label ? "border-brand-accent bg-brand-accent/10 text-brand-text" : "border-brand-line text-brand-sec hover:text-brand-text"}`}>
                      {lv.label}{lv.cefr !== "—" && <span className="ml-1 text-[11px] text-brand-muted">{lv.cefr}</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2">
              <input data-testid="cv-lang-custom" className={`${field} max-w-xs`} placeholder="Inny język" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { e.preventDefault(); addItem("languages", { name: e.target.value.trim(), level: "" }); e.target.value = ""; } }} />
              <span className="text-[13px] text-brand-muted">Wpisz i naciśnij Enter</span>
            </div>
          </div>
        )}

        {step.key === "extras" && (
          <div className="space-y-6">
            <TagAdder label="Kursy" testId="cv-courses" values={cv.courses || []} onAdd={(v) => addTag("courses", v)} onDel={(v) => delTag("courses", v)} optional placeholder="np. Kurs Google Analytics — Enter" />
            <TagAdder label="Certyfikaty" testId="cv-certificates" values={cv.certificates || []} onAdd={(v) => addTag("certificates", v)} onDel={(v) => delTag("certificates", v)} optional placeholder="np. Certyfikat PRINCE2 — Enter" />
            <TagAdder label="Uprawnienia" testId="cv-licenses" values={cv.licenses || []} onAdd={(v) => addTag("licenses", v)} onDel={(v) => delTag("licenses", v)} optional placeholder="np. Uprawnienia SEP — Enter" />
            <div>
              <p className={label}>Prawo jazdy {optional}</p>
              <div className="flex flex-wrap gap-2" data-testid="cv-driving">
                {DRIVING_CATEGORIES.map((cat) => {
                  const on = (cv.driving || []).includes(cat);
                  return <button key={cat} type="button" data-testid={`cv-driving-${cat}`} onClick={() => (on ? delTag("driving", cat) : addTag("driving", cat))} className={`rounded-field border px-3 py-2 text-[13px] font-medium transition-colors ${on ? "border-brand-accent bg-brand-accent/10 text-brand-text" : "border-brand-line text-brand-sec hover:text-brand-text"}`}>{on && "✓ "}{cat}</button>;
                })}
              </div>
            </div>
            <TagAdder label="Projekty" testId="cv-projects" values={cv.projects || []} onAdd={(v) => addTag("projects", v)} onDel={(v) => delTag("projects", v)} optional placeholder="np. Wolontariat w fundacji — Enter" />
            <TagAdder label="Zainteresowania" testId="cv-interests" values={cv.interests || []} onAdd={(v) => addTag("interests", v)} onDel={(v) => delTag("interests", v)} optional placeholder="np. Fotografia — Enter" />
          </div>
        )}

        {step.key === "review" && (
          done ? (
            <div data-testid="cv-prepare-done" className="rounded-panel border border-brand-accent/40 bg-brand-accent/[0.06] p-6">
              <div className="flex items-center gap-2 text-brand-accent"><Sparkles size={18} /><span className="font-display text-base font-semibold">Dane zebrane — CV gotowe do przygotowania.</span></div>
              <p className="mt-3 text-sm text-brand-sec">Zapisaliśmy wersję roboczą. Zobacz podgląd A4 w szablonie ATS i pobierz plik PDF lub DOCX. Nie dopisaliśmy żadnych informacji poza tym, co podałeś.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="button" data-testid="cv-goto-template" onClick={() => navigate(`/cv/szablon/${cv.id}`)} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"><FileText size={16} /> Zobacz szablon i pobierz</button>
                <button type="button" data-testid="cv-goto-my" onClick={() => navigate("/cv/moje")} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-5 py-2.5 text-sm font-semibold text-brand-text hover:border-brand-accent/60">Przejdź do „Moje CV" <ArrowRight size={15} /></button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 1. Język dokumentu */}
              <div>
                <label className={label}>Język dokumentu CV</label>
                <p className="mb-2 text-[13px] text-brand-muted">To język, w jakim przygotujemy CV (steruje generowanymi treściami). Nie tłumaczymy automatycznie już wpisanych treści bez Twojego potwierdzenia.</p>
                <TileGroup testId="cv-doclang" columns="sm:grid-cols-3" options={DOC_LANGS} value={cv.doc_lang || "Polski"} onChange={(v) => patchLocal({ doc_lang: v })} allowOther otherValue={cv.doc_lang && !DOC_LANGS.includes(cv.doc_lang) ? cv.doc_lang : ""} onOther={(v) => patchLocal({ doc_lang: v || "Inne" })} />
              </div>

              {/* 2. Podsumowanie zawodowe — dobrze wyeksponowane, z „Pomóż mi napisać" */}
              <div className="rounded-panel border border-brand-accent/30 bg-brand-accent/[0.04] p-5" data-testid="cv-summary-block">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <label className={`${label} mb-0 text-base`}>Podsumowanie zawodowe {optional}</label>
                  <div className="flex items-center gap-3">
                    {summaryUndo !== null && (
                      <button type="button" data-testid="cv-assist-undo-summary" onClick={undoSummary} className="inline-flex items-center gap-1.5 text-[13px] text-brand-muted hover:text-brand-text"><Undo2 size={13} /> Cofnij zmianę</button>
                    )}
                    <button type="button" data-testid="cv-assist-open-summary" onClick={() => setAssist({ field: "summary", expIndex: null })} className="inline-flex items-center gap-1.5 rounded-full bg-brand-accent px-3.5 py-2 text-[13px] font-semibold text-brand-on transition-transform hover:-translate-y-0.5"><Wand2 size={14} /> Pomóż mi napisać</button>
                  </div>
                </div>
                <p className="mb-3 text-[13px] text-brand-sec">To krótki tekst na początku CV, który pokazuje pracodawcy Twoje doświadczenie, najważniejsze umiejętności i cel zawodowy.</p>
                <textarea data-testid="cv-summary-input" className={field} rows={4} value={cv.summary || ""} onChange={(e) => onSummaryChange(e.target.value)} placeholder="Napisz sam lub kliknij „Pomóż mi napisać”. 2–4 zdania." />
                <p className="mt-2 flex items-center gap-2 text-[13px] text-brand-muted"><ShieldCheck size={14} className="text-brand-accent" /> Do CV trafi wyłącznie to, co tu wpiszesz — niczego nie dopisujemy. Możesz też edytować propozycję po wstawieniu.</p>
              </div>

              {/* 3. Podgląd wszystkich sekcji z możliwością powrotu do edycji */}
              <ReviewPanel cv={cv} onEdit={(i) => setStepIndex(i)} />

              <p className="text-[13px] text-brand-muted">Po sprawdzeniu danych kliknij „Przygotuj CV", aby przejść do szablonu i pobrania.</p>
            </div>
          )
        )}
      </div>

      {/* Nawigacja */}
      {!done && (
        <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-brand-line pt-5">
          <button type="button" data-testid="cv-back" onClick={goBack} disabled={stepIndex === 0} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-5 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60 disabled:opacity-40"><ArrowLeft size={15} /> Wstecz</button>
          <button type="button" data-testid="cv-save-later" onClick={saveLater} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-5 py-2.5 text-sm font-semibold text-brand-sec transition-colors hover:text-brand-text"><Save size={15} /> Zapisz i dokończ później</button>
          <div className="ml-auto">
            {step.key === "review" ? (
              <button type="button" data-testid="cv-prepare" onClick={prepare} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-6 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"><Sparkles size={16} /> Przygotuj CV</button>
            ) : (
              <button type="button" data-testid="cv-next" onClick={goNext} disabled={!canNext} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-6 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-40">Dalej <ArrowRight size={15} /></button>
            )}
          </div>
        </div>
      )}

      {assist && (
        <CvWriteAssist
          open={!!assist}
          onClose={() => setAssist(null)}
          cvId={cv.id}
          field={assist.field}
          expIndex={assist.expIndex}
          onEvent={(ev) => logAssist(ev, assist.field)}
          onInsert={(text) => { if (assist.field === "summary") insertSummary(text); else insertExpBullets(assist.expIndex, text); }}
        />
      )}
    </div>
  );
}

const TagAdder = ({ label: lbl, testId, values, onAdd, onDel, placeholder, optional: opt }) => {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onAdd(val); setVal(""); } };
  return (
    <div>
      <p className={label}>{lbl}{opt && optional}</p>
      <div className="flex gap-2">
        <input data-testid={`${testId}-input`} className={field} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} />
        <button type="button" data-testid={`${testId}-add`} onClick={add} className="shrink-0 rounded-button border border-brand-line px-4 text-sm font-semibold text-brand-text hover:border-brand-accent/60">Dodaj</button>
      </div>
      {values.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testId}-list`}>
          {values.map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-brand-elevated px-3 py-1.5 text-[13px] text-brand-text">
              {s}<button type="button" onClick={() => onDel(s)} className="text-brand-muted hover:text-red-300"><Trash2 size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const Row = ({ k, v }) => (v ? <div className="flex gap-2 text-sm"><span className="w-40 shrink-0 text-brand-muted">{k}</span><span className="text-brand-text">{v}</span></div> : null);

const SectionHead = ({ title, step, onEdit }) => (
  <div className="flex items-center justify-between">
    <p className="text-[13px] font-semibold uppercase tracking-wide text-brand-muted">{title}</p>
    {onEdit && <button type="button" data-testid={`cv-review-edit-${step}`} onClick={() => onEdit(step)} className="text-[12.5px] text-brand-accent hover:underline">Edytuj</button>}
  </div>
);

const ReviewPanel = ({ cv, onEdit }) => {
  const b = cv.basics || {};
  const situationLabel = (SITUATIONS.find((s) => s.key === cv.situation)?.label) || (cv.situation === "Inne" ? cv.situation_other : cv.situation);
  return (
    <div data-testid="cv-review" className="space-y-5 rounded-panel border border-brand-line bg-brand-surface p-6">
      <p className="text-sm text-brand-sec">Sprawdź zebrane informacje. Do CV trafi dokładnie to, co widzisz poniżej — nic nie dopisujemy. Każdą sekcję możesz poprawić przyciskiem „Edytuj".</p>
      <div className="space-y-2.5">
        <div><SectionHead title="Sytuacja i cel" step={0} onEdit={onEdit} />
          <div className="mt-1 space-y-1">
            <Row k="Sytuacja" v={situationLabel} />
            <Row k="Cel / stanowisko" v={[cv.target_role, cv.target_industry, cv.target_location, cv.work_mode].filter(Boolean).join(" · ")} />
          </div>
        </div>
        <div><SectionHead title="Dane kontaktowe" step={2} onEdit={onEdit} />
          <div className="mt-1 space-y-1">
            <Row k="Imię i nazwisko" v={[b.first_name, b.last_name].filter(Boolean).join(" ")} />
            <Row k="Kontakt" v={[b.email, b.phone, b.city].filter(Boolean).join(" · ")} />
            <Row k="Język dokumentu" v={cv.doc_lang} />
          </div>
        </div>
      </div>
      {(cv.experience || []).length > 0 && (
        <div><SectionHead title="Doświadczenie" step={3} onEdit={onEdit} />
          {cv.experience.map((x, i) => <p key={i} className="mt-1 text-sm text-brand-text">{[x.role, x.company].filter(Boolean).join(" — ")} <span className="text-brand-muted">{[x.start, x.end].filter(Boolean).join(" – ")}</span></p>)}
        </div>
      )}
      {(cv.education || []).length > 0 && (
        <div><SectionHead title="Wykształcenie" step={4} onEdit={onEdit} />
          {cv.education.map((x, i) => <p key={i} className="mt-1 text-sm text-brand-text">{[x.school, x.field].filter(Boolean).join(" — ")} <span className="text-brand-muted">{[x.start, x.end, x.status].filter(Boolean).join(" · ")}</span></p>)}
        </div>
      )}
      {(cv.skills || []).length > 0 && <div><SectionHead title="Umiejętności" step={5} onEdit={onEdit} /><p className="mt-1 text-sm text-brand-text">{cv.skills.join(", ")}</p></div>}
      {(cv.tools || []).length > 0 && <div><SectionHead title="Narzędzia" step={5} onEdit={onEdit} /><p className="mt-1 text-sm text-brand-text">{cv.tools.map((t) => [t.name, t.level].filter(Boolean).join(" — ")).join(", ")}</p></div>}
      {(cv.languages || []).length > 0 && <div><SectionHead title="Języki" step={6} onEdit={onEdit} /><p className="mt-1 text-sm text-brand-text">{cv.languages.map((l) => [l.name, l.level].filter(Boolean).join(" — ")).join(", ")}</p></div>}
      {(cv.courses || []).length + (cv.certificates || []).length + (cv.licenses || []).length + (cv.driving || []).length + (cv.projects || []).length + (cv.interests || []).length > 0 && (
        <div><SectionHead title="Dodatkowe" step={7} onEdit={onEdit} />
          <p className="mt-1 text-sm text-brand-text">{[...(cv.courses || []), ...(cv.certificates || []), ...(cv.licenses || []), ...(cv.driving || []).map((d) => `Prawo jazdy ${d}`), ...(cv.projects || []), ...(cv.interests || [])].join(", ")}</p>
        </div>
      )}
      {cv.summary && <div><p className="text-[13px] font-semibold uppercase tracking-wide text-brand-muted">Podsumowanie</p><p className="mt-1 text-sm text-brand-text">{cv.summary}</p></div>}
    </div>
  );
};
